import { Link, useLocation } from 'react-router-dom'
import { ConfigProvider, Menu, Popover, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { getCriticalConfig } from 'src/app/store/masterSetup'
import { IModuleItem, ISubmoduleItem } from 'src/app/shared/models/store.model'
import {
  ExceptionsSVG,
  TransactionReportsSVG,
  TransactionsRulesSVG,
  BusinessAdminLogsSVG,
  BusinessAdministrationSVG,
  ClientSetupSVG,
  CollapseIcon,
  ExpandIcon,
} from 'src/assets/icons'
import { useEffect, useState } from 'react'
import { getSelectedPageKey } from 'src/app/store/common'
import { colorText, colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import { sortByOrder } from 'src/app/shared/utils'
import './index.scss'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'

const getMenuIcon = (moduleName: string) => {
  let icon = null
  switch (moduleName) {
    case 'Exceptions':
      icon = <ExceptionsSVG />
      break
    case 'Transactions & Rules':
      icon = <TransactionsRulesSVG />
      break
    case 'Business Administration':
      icon = <BusinessAdministrationSVG />
      break
    case 'Client Setup':
      icon = <ClientSetupSVG />
      break
    case 'Transaction Reports':
      icon = <TransactionReportsSVG />
      break
    case 'Business Admin Logs':
      icon = <BusinessAdminLogsSVG />
      break
    case 'IT System Configuration':
      icon = <ExceptionsSVG />
      break
    default:
      icon = <ExceptionsSVG />
  }
  return icon
}

interface Props {
  isMenuMinimized: boolean
  openKeys: string[]
  setOpenKeys: (value: React.SetStateAction<string[]>) => void
}

function PanelContent(props: Props) {
  const { isMenuMinimized, openKeys, setOpenKeys } = props
  const location = useLocation()
  const { userPermissions } = useRolePermission()

  const selectedPageKey = useSelector(getSelectedPageKey)
  const criticalConfig = useSelector(getCriticalConfig)
  const [selectedMenuItemKey, setSelectedMenuItemKey] = useState<any[]>([])
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  const items: IModuleItem[] = sortByOrder([...(criticalConfig?.moduleItemList || [])], 'orderIndex')

  useEffect(() => {
    const selectedPageIndex = items?.find((item: IModuleItem) => {
      const subModuleList = item?.submoduleItemList
      const selectedSubModules = subModuleList?.find((subModule) => subModule.submoduleName === selectedPageKey)
      return !!selectedSubModules?.id
    })?.id

    if (isFirstLoad && selectedPageIndex && !openKeys?.includes(selectedPageIndex))
      setOpenKeys((prev) => [...prev, selectedPageIndex])
    setIsFirstLoad(false)
  }, [items, selectedPageKey, setOpenKeys, openKeys, isFirstLoad])

  const {
    leftNavigationPanelClickedItemBackgroundColor: panelClickedItemBgColor,
    leftNavigationPanelClickedItemFontColor: panelClickedItemFontColor,
    leftNavigationPanelFontColor: panelFontColor,
    leftNavigationPanelHoveroverBackgroundColor: panelHoverBgColor,
    leftNavigationPanelHoveroverFontColor: panelHoverFontColor,
  } = criticalConfig?.systemColor || {}

  const handleMenuItems = (keys: Array<string>) => {
    setOpenKeys(keys)
  }

  const handleClickSubmenuItem = (submenuItem: any) => {
    setSelectedMenuItemKey([submenuItem?.id])
  }

  const isShowMainMenu = (submoduleItemList: Array<any>) => {
    let isShow = false

    submoduleItemList?.forEach((item: any) => {
      if (userPermissions[item?.submoduleCodeName as ESubModules]?.includes(EPermissions.VIEW_ITEMS)) {
        isShow = true
        return
      }
    })
    return isShow
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemColor: panelFontColor,
            itemHoverColor: panelHoverFontColor,
            itemHoverBg: panelHoverBgColor,
          },
        },
      }}
    >
      <Menu
        mode='inline'
        openKeys={openKeys}
        onOpenChange={handleMenuItems}
        selectable
        selectedKeys={selectedMenuItemKey}
        style={{ background: 'inherit' }}
        expandIcon={(expandProps: any) => {
          const { isOpen } = expandProps
          return isOpen ? <CollapseIcon /> : <ExpandIcon />
        }}
      >
        {items?.map((item) =>
          isShowMainMenu(item?.submoduleItemList) ? (
            <Menu.SubMenu
              key={item.id}
              icon={getMenuIcon(item.moduleName)}
              title={item.moduleName}
              className={!isMenuMinimized ? 'panel-open' : ''}
              style={
                // background color for selected menu module item when minimized
                isMenuMinimized &&
                item?.submoduleItemList?.find((submenuItem) => selectedPageKey === submenuItem.submoduleName)
                  ? {
                      backgroundColor: 'panelClickedItemBgColor',
                      color: panelClickedItemFontColor,
                      borderTopLeftRadius: '20px',
                      borderBottomLeftRadius: '20px',
                      borderBottomRightRadius: '0',
                      borderTopRightRadius: '0',
                    }
                  : {}
              }
            >
              {isMenuMinimized ? (
                <Menu.ItemGroup
                  key={item.id}
                  style={{ fontWeight: '400', color: colorTextSubtle }}
                  title={
                    <Typography.Paragraph
                      style={{
                        fontWeight: '700',
                        color: colorText,
                        margin: '0',
                        padding: '16px 0 0 16px ',
                      }}
                    >
                      {item.moduleName}
                    </Typography.Paragraph>
                  }
                >
                  {sortByOrder([...item?.submoduleItemList], 'orderIndex')?.map((submenuItem: ISubmoduleItem) =>
                    userPermissions[submenuItem?.submoduleCodeName as ESubModules]?.includes(
                      EPermissions.VIEW_ITEMS,
                    ) ? (
                      <Menu.Item
                        key={submenuItem.id}
                        className='minimizedItem'
                        onClick={() => handleClickSubmenuItem(submenuItem)}
                      >
                        {<Link to={submenuItem.pageUrl}>{submenuItem.submoduleName}</Link>}
                      </Menu.Item>
                    ) : null,
                  )}
                </Menu.ItemGroup>
              ) : (
                <div>
                  {sortByOrder([...item?.submoduleItemList], 'orderIndex')?.map((submenuItem: ISubmoduleItem) =>
                    userPermissions[submenuItem?.submoduleCodeName as ESubModules]?.includes(
                      EPermissions.VIEW_ITEMS,
                    ) ? (
                      <Popover
                        key={submenuItem.id}
                        placement='right'
                        content={submenuItem.tooltip}
                        overlayStyle={{ maxWidth: '220px' }}
                      >
                        <Menu.Item
                          key={submenuItem.id}
                          style={
                            (((location.state as any)?.returnUrl 
                              ? (location.state as any)?.returnUrl?.includes(submenuItem.pageUrl)
                              : location.pathname?.includes(submenuItem.pageUrl)) && 
                              !isMenuMinimized)
                              ? {
                                  backgroundColor: panelClickedItemBgColor,
                                  fontWeight: '700',
                                  // pointerEvents: 'none',
                                  color: panelClickedItemFontColor,
                                }
                              : {}
                          }
                          onClick={() => handleClickSubmenuItem(submenuItem)}
                        >
                          {<Link to={submenuItem.pageUrl}>{submenuItem.submoduleName}</Link>}
                        </Menu.Item>
                      </Popover>
                    ) : null,
                  )}
                </div>
              )}
            </Menu.SubMenu>
          ) : null,
        )}
      </Menu>
    </ConfigProvider>
  )
}

export default PanelContent
